import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import styles from './TooltipContainer.module.scss';
import { usePopper } from 'react-popper';
import type { Modifier } from '@popperjs/core';
import { createPortal } from 'react-dom';

type Props = {
  children?: React.ReactNode;
  renderReferenceComponent: (className: string, ref: any) => ReactNode,
  style?: React.CSSProperties,
  className?: string,
  delayed?: boolean,
}

const hideArrowWhenNotPointingInCenter: Partial<Modifier<string, object>> = {
  name: 'applyArrowHide',
  enabled: true,
  phase: 'write',
  fn({ state }) {
    const { arrow } = state.elements;

    if (arrow) {
      if (state.modifiersData.arrow && state.modifiersData.arrow.centerOffset !== 0) {
        arrow.setAttribute('data-hide', '');
      } else {
        arrow.removeAttribute('data-hide');
      }
    }
  },
};

const moveArrowWhenFlipping: Partial<Modifier<string, object>> = {
  name: 'moveArrowWhenFlipping',
  enabled: true,
  phase: 'write',
  fn({ state }) {
    const placement = state.placement;
    const { arrow } = state.elements;
    if (arrow) {
      if (placement === 'bottom') {
        arrow.setAttribute('data-top', '');
        arrow.removeAttribute('data-right');
        arrow.removeAttribute('data-bottom');
        arrow.removeAttribute('data-left');
      } else if (placement === 'top') {
        arrow.setAttribute('data-bottom', '');
        arrow.removeAttribute('data-top');
        arrow.removeAttribute('data-right');
        arrow.removeAttribute('data-left');
      } else if (placement === 'left') {
        arrow.setAttribute('data-right', '');
        arrow.removeAttribute('data-top');
        arrow.removeAttribute('data-bottom');
        arrow.removeAttribute('data-left');
      } else if (placement === 'right') {
        arrow.setAttribute('data-left', '');
        arrow.removeAttribute('data-top');
        arrow.removeAttribute('data-right');
        arrow.removeAttribute('data-bottom');
      }
    }
  }
}

const TooltipContainer: FC<Props> = ({ children, renderReferenceComponent, className, delayed, }) => {
  const [referenceElement, setReferenceElement] = useState<HTMLDivElement | null>(null);
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);
  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  const hoverTimeout = useRef<NodeJS.Timeout | null>(null);

  const { styles: popperStyles, attributes, forceUpdate } = usePopper(referenceElement, popperElement, {
    placement: 'top',
    modifiers: [
      {
        name: 'arrow',
        options: {
          element: arrowElement,
          padding: 8,
        }
      }, {
        name: 'offset',
        options: {
          offset: [0, 13],
        },
      },
      hideArrowWhenNotPointingInCenter,
      moveArrowWhenFlipping,
      {
        name: 'eventListeners',
        options: {
          scroll: false,
          resize: false,
        }
      },
    ],
  });

  useEffect(() => {
    if (forceUpdate && referenceElement && visible) {
      requestAnimationFrame(() => {
        forceUpdate();
      });
    }
  }, [visible, forceUpdate, referenceElement]);

  useEffect(() => {
    const show = () => {
      if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
      setVisible(true);
    };

    const hide = () => {
      hoverTimeout.current = setTimeout(() => setVisible(false), 100);
    };

    const refEl = referenceElement;
    const popEl = popperElement;

    if (refEl) {
      refEl.addEventListener('mouseenter', show);
      refEl.addEventListener('mouseleave', hide);
    }

    if (popEl) {
      popEl.addEventListener('mouseenter', show);
      popEl.addEventListener('mouseleave', hide);
    }

    return () => {
      if (refEl) {
        refEl.removeEventListener('mouseenter', show);
        refEl.removeEventListener('mouseleave', hide);
      }
      if (popEl) {
        popEl.removeEventListener('mouseenter', show);
        popEl.removeEventListener('mouseleave', hide);
      }
    };
  }, [referenceElement, popperElement]);

  return (
    <>
      {renderReferenceComponent && renderReferenceComponent(styles.referenceContainer, setReferenceElement)}

      {children && createPortal(
        <div ref={setPopperElement} className={`${styles.popperContainer} ${visible ? styles.visible : ''} ${className || ''} ${delayed ? styles.delayed : ''}`} style={popperStyles.popper} {...attributes.popper}>
          {children}
          <div ref={setArrowElement} className={styles.arrow} style={popperStyles.arrow} />
        </div>,
        document.body
      )}
    </>
  );
}

export default TooltipContainer;
